import React, { useState, useEffect } from "react";
import { EditButton, ShowButton } from "@refinedev/antd";
import { useList, useCustomMutation, useNotification } from "@refinedev/core";
import { Table, Space, Tag, Button, Tooltip, Modal, Form, Select, Input, message, Card } from "antd";
import { PrinterOutlined, SyncOutlined, PlusOutlined, FileTextOutlined, SearchOutlined } from "@ant-design/icons";
import { useNavigate, Link } from "react-router-dom";
import { PageHeader } from "../../components/ui";
import { invoiceRegenerationService, RegenerationRequest } from "../../api/invoiceRegenerationService";

const { TextArea } = Input;

interface SaleOrder {
    id: number;
    invoiceNo: string;
    customerName: string;
    orderDate: string;
    deliveryDate: string;
    status: string;
    totalAmount: number;
    documentUrl?: string;
    createdAt: string;
}

export const SaleOrderList: React.FC = () => {
    const [searchText, setSearchText] = useState<string>("");
    const [filteredItems, setFilteredItems] = useState<SaleOrder[]>([]);
    const [regenerateModalVisible, setRegenerateModalVisible] = useState(false);
    const [selectedSaleOrder, setSelectedSaleOrder] = useState<SaleOrder | null>(null);
    const [regenerating, setRegenerating] = useState(false);
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const { open } = useNotification();

    const { data, isLoading, refetch } = useList({
        resource: "saleorders",
    });

    const { mutate: confirm } = useCustomMutation();
    const { mutate: cancel } = useCustomMutation();
    const { mutate: convert } = useCustomMutation();
    const { mutate: generatePdf } = useCustomMutation();

    useEffect(() => {
        if (!data?.data) return;

        let orders = data.data as SaleOrder[];

        if (searchText.trim() !== "") {
            const searchLower = searchText.toLowerCase();
            orders = orders.filter((order: SaleOrder) =>
                order.invoiceNo?.toLowerCase().includes(searchLower) ||
                order.customerName?.toLowerCase().includes(searchLower) ||
                order.status?.toLowerCase().includes(searchLower)
            );
        }

        const sorted = [...orders].sort((a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );

        setFilteredItems(sorted);
    }, [data, searchText]);

    const handleConfirm = (id: number) => {
        confirm({ url: `SaleOrders/${id}/confirm`, method: "post" }, { onSuccess: () => refetch() });
    };

    const handleCancel = (id: number) => {
        cancel({ url: `SaleOrders/${id}/cancel`, method: "post" }, { onSuccess: () => refetch() });
    };

    const handleConvert = (id: number) => {
        convert({ url: `SaleOrders/${id}/convert`, method: "post" }, { onSuccess: () => refetch() });
    };

    const handlePrint = (record: SaleOrder) => {
        generatePdf({ url: `SaleOrders/${record.id}/pdf`, method: "get" }, {
            onSuccess: (data: any) => {
                const documentUrl = data?.data?.documentUrl || data?.documentUrl;
                if (documentUrl) {
                    refetch();
                    window.open(documentUrl, '_blank');
                }
            },
        });
    };

    const handleRegenerateSubmit = async (values: any) => {
        if (!selectedSaleOrder) return;
        setRegenerating(true);
        try {
            const request: RegenerationRequest = {
                regenerationType: values.regenerationType,
                reason: values.reason,
                preserveOriginal: true,
                notifyCustomer: false,
            };
            const result = await invoiceRegenerationService.regenerateSaleOrder(selectedSaleOrder.id, request);
            if (result.success) {
                message.success(`Proforma ${selectedSaleOrder.invoiceNo} regenerated!`);
                setRegenerateModalVisible(false);
                refetch();
            } else {
                message.error(`Failed: ${result.errorMessage}`);
            }
        } catch (error: any) {
            message.error(`Failed: ${error.message}`);
        } finally {
            setRegenerating(false);
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case "Confirmed": return "success";
            case "Draft": return "default";
            case "Converted": return "purple";
            case "Cancelled": return "error";
            default: return "default";
        }
    };

    const columns = [
        {
            title: "Proforma #",
            dataIndex: "invoiceNo",
            key: "invoiceNo",
            render: (text: string, record: SaleOrder) => (
                <Link to={`/sale-orders/show/${record.id}`} className="font-medium text-primary hover:underline">
                    {text || "-"}
                </Link>
            ),
        },
        {
            title: "Customer",
            dataIndex: "customerName",
            key: "customerName",
            render: (value: string) => (
                <span className="text-text-secondary">{value || "-"}</span>
            ),
        },
        {
            title: "Date",
            dataIndex: "orderDate",
            key: "orderDate",
            render: (value: string) => (
                <span className="text-text-secondary text-sm">
                    {value ? new Date(value).toLocaleDateString() : "-"}
                </span>
            ),
            responsive: ['md'] as any,
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (value: string) => (
                <Tag color={getStatusColor(value)} className="status-tag">{value}</Tag>
            ),
        },
        {
            title: "Amount",
            dataIndex: "totalAmount",
            key: "totalAmount",
            render: (value: number) => (
                <span className="font-semibold text-primary">${value?.toFixed(2) || '0.00'}</span>
            ),
        },
        {
            title: "Actions",
            key: "actions",
            width: 250,
            render: (_: any, record: SaleOrder) => (
                <Space size="small" wrap>
                    <EditButton hideText size="small" recordItemId={record.id} resource="saleorders" disabled={record.status !== "Draft"} />
                    <ShowButton hideText size="small" recordItemId={record.id} resource="saleorders" />
                    <Tooltip title="Print">
                        <Button size="small" icon={<PrinterOutlined />} onClick={() => handlePrint(record)} />
                    </Tooltip>
                    {record?.documentUrl && (
                        <Tooltip title="Regenerate">
                            <Button size="small" icon={<SyncOutlined />} onClick={() => { setSelectedSaleOrder(record); setRegenerateModalVisible(true); }} />
                        </Tooltip>
                    )}
                    {record.status === "Draft" && (
                        <Button size="small" type="primary" onClick={() => handleConfirm(record.id)}>Confirm</Button>
                    )}
                    {(record.status === "Draft" || record.status === "Confirmed") && (
                        <Button size="small" danger onClick={() => handleCancel(record.id)}>Cancel</Button>
                    )}
                    {record.status === "Confirmed" && (
                        <Button size="small" type="primary" onClick={() => handleConvert(record.id)}>Convert</Button>
                    )}
                </Space>
            ),
        },
    ];

    return (
        <div className="page-container">
            <PageHeader
                title="Proforma Invoices"
                icon={<FileTextOutlined />}
                breadcrumbs={[{ title: "Proforma Invoices" }]}
                actions={[
                    {
                        label: "Create",
                        onClick: () => navigate("/sale-orders/create"),
                        type: "primary",
                        icon: <PlusOutlined />,
                    },
                ]}
            />

            <div className="data-table-card">
                <Card>
                    <div style={{ marginBottom: 16 }}>
                        <Input
                            placeholder="Search by proforma number, customer, or status"
                            prefix={<SearchOutlined className="text-text-muted" />}
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            style={{ maxWidth: 400 }}
                            allowClear
                            size="large"
                        />
                    </div>

                    <Table
                        dataSource={filteredItems}
                        columns={columns}
                        rowKey="id"
                        loading={isLoading}
                        pagination={{
                            pageSize: 10,
                            showSizeChanger: true,
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
                        }}
                        className="custom-table"
                        scroll={{ x: 900 }}
                    />
                </Card>
            </div>

            <Modal
                title={`Regenerate Proforma ${selectedSaleOrder?.invoiceNo || ""}`}
                open={regenerateModalVisible}
                onCancel={() => { setRegenerateModalVisible(false); setSelectedSaleOrder(null); }}
                footer={null}
                width={500}
            >
                <Form form={form} layout="vertical" onFinish={handleRegenerateSubmit} initialValues={{ regenerationType: "BugFix" }}>
                    <Form.Item name="regenerationType" label="Regeneration Type" rules={[{ required: true }]}>
                        <Select>
                            <Select.Option value="BugFix">Bug Fix</Select.Option>
                            <Select.Option value="LayoutUpdate">Layout Update</Select.Option>
                            <Select.Option value="Manual">Manual</Select.Option>
                        </Select>
                    </Form.Item>
                    <Form.Item name="reason" label="Reason (Optional)">
                        <TextArea rows={3} placeholder="Brief explanation" />
                    </Form.Item>
                    <Form.Item>
                        <Space>
                            <Button type="primary" htmlType="submit" loading={regenerating}>Regenerate</Button>
                            <Button onClick={() => setRegenerateModalVisible(false)}>Cancel</Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};
